import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import sortBy from 'lodash/sortBy';
import { fetchTagsReducer, initialTagsState } from '~/reducers/marketing';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';
import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import {
  FlatImageEditorHandle,
  FlatImageEditorPendingUploadState,
  FlatImageEditorProps,
  FlatImageUploadPayload,
} from '~/components/Common/Images/types';
import { Badge, Spinner } from '~/components/Experiences/components';
import { getPreviewURL } from '~/components/Experiences/helpers';
import { useUpdateExperienceValues } from '~/components/Experiences/hooks';

import {
  DEAL_DESCRIPTION,
  EXP_PROVIDER_ASPIRE,
  EXP_PROVIDER_COLLINSON,
  EXP_PROVIDER_CUSTOMLINC,
  EXP_PROVIDER_DERBYSOFT,
  EXP_PROVIDER_HUBBY,
  EXP_PROVIDER_KLOOK,
  EXP_PROVIDER_LED,
  EXP_PROVIDER_REZDY,
  IMAGES,
  INCLUDED,
  ORIGINAL_EXPERIENCE_CONTENT_TAB,
  SHORT_DESCRIPTION,
  THINGS_TO_KNOW,
  TITLE,
  curationCommentOptions,
  getStatusByCurated,
} from '~/consts/experiences';
import { TAG_TYPE_CAMPAIGN } from '~/consts/tags';

import {
  CurateExperienceParams,
  CurationStatus,
  ExperienceOffer,
  curationExperience,
  deleteCollectionOfferLink,
} from '~/services/ExperiencesService';
import { getUploadedImageId, uploadImage } from '~/services/ImageService';
import offersService from '~/services/OffersService';

import getErrorMessage from '~/utils/getErrorMessage';

import ExperienceCurationImages from './ExperienceCurationImages';
import ExperienceCurationOverview from './ExperienceCurationOverview';
import ExperienceOfferContentCopyModal, { EditableOfferDetails } from './ExperienceOfferContentCopyModal';
import ExperienceOfferVendorUpdatedWarningModal from './ExperienceOfferVendorUpdatedWarningModal';

// Temporary type extension until contract is published with curationComment field
type ExperienceOfferWithCurationComment = ExperienceOffer & {
  curationComment?: string;
  heroImageId?: string;
};

interface Props {
  experience: ExperienceOfferWithCurationComment;
  loading: boolean;
  tenant: App.Tenant;
  createExperience: ({ curationStatus }: CurateExperienceParams) => Promise<void>;
  imageEditorRef?: React.Ref<FlatImageEditorHandle>;
  onPendingUploadStateChange?: (state: FlatImageEditorPendingUploadState) => void;
  /** Optional panel rendered beside the curation fields (e.g. umbrella grouped offers) */
  sidePanel?: React.ReactNode;
}

type CurationStatusProps = {
  label: string;
  value: CurationStatus | undefined;
};

const SELECT_ALL_OPTION = 'All';

export default function ExperienceCurationOptions({
  experience,
  createExperience,
  tenant,
  imageEditorRef,
  onPendingUploadStateChange,
  sidePanel,
}: Props) {
  const [images, setImages] = useState<Array<Experiences.Image>>([]);
  const [imageHero, setImageHero] = useState<string | number | null>(null);
  const [imagesToRemove, setImagesToRemove] = useState<Array<string>>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Array<string>>([]);
  const [selectedExcludedRegions, setSelectedExcludedRegions] = useState<Array<string>>([]);
  const [selectedCollections, setSelectedCollections] = useState<Array<{ id: string; theme: string }>>([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [vendorInfoChangedModalOpen, setVendorInfoChangedModalOpen] = useState(false);
  const [editMultiSelect, setEditMultiSelect] = useState({
    campaigns: false,
    regions: false,
  });
  const [collectionsError, setCollectionsError] = useState<string | null>(null);
  const [curationStatusValue, setCurationStatusValue] = useState<CurationStatusProps>({
    label: 'Not Curated',
    value: 'NOT_CURATED',
  });
  const [showCommissionMarginField, setShowCommissionMarginField] = useState<boolean>(false);

  const { updateValues, values, payload, contentTab } = useUpdateExperienceValues();

  const areOriginalValues = contentTab === ORIGINAL_EXPERIENCE_CONTENT_TAB;

  const statusLabel = useMemo(
    () => ({
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      NOT_CURATED: 'Not Curated',
    }),
    [],
  );

  useEffect(() => {
    if (experience) {
      const { campaigns = [], excludedRegions = [], images = [], collections = [] } = experience;

      if (campaigns.length) {
        setSelectedCampaigns(campaigns);
        setEditMultiSelect((prev) => ({ ...prev, campaigns: true }));
      }

      if (excludedRegions.length) {
        setSelectedExcludedRegions(excludedRegions.map((region) => region.regionCode));
        setEditMultiSelect((prev) => ({ ...prev, regions: true }));
      }

      if (collections.length) {
        setSelectedCollections(collections);
      }

      if (experience?.curationStatus) {
        setCurationStatusValue({
          label: statusLabel[experience.curationStatus],
          value: experience.curationStatus,
        });
      }

      if (images.length) {
        setImages(images);
      }

      setImageHero(experience.heroImageId ?? images.find((image) => image.isHero)?.id ?? null);
      setImagesToRemove([]);

      if (experience.commissionMargin) {
        setShowCommissionMarginField(true);
      }
    }
  }, [experience, payload?.categories, statusLabel]);

  const [campaigns, campaignsDispatch] = useReducer(fetchTagsReducer, initialTagsState);

  const fetchCampaigns = async (): Promise<void> => {
    campaignsDispatch({ type: 'LOAD' });
    try {
      const res = await offersService.getTags(TAG_TYPE_CAMPAIGN);
      campaignsDispatch({
        type: 'FETCH',
        payload: sortBy(res.result, ['name']),
      });
    } catch (error) {
      campaignsDispatch({ type: 'ERROR', message: getErrorMessage(error) });
    }
  };

  useEffect(() => {
    if (!areOriginalValues) {
      fetchCampaigns();
    }
  }, [areOriginalValues]);

  const handleDeleteCollection = async (collectionId: string): Promise<void> => {
    try {
      await deleteCollectionOfferLink({ collectionId, offerId: experience.id });
      setSelectedCollections((prev) => prev.filter((col) => col.id !== collectionId));
    } catch (error) {
      setCollectionsError(getErrorMessage(error, 'Failed to delete collection link'));
    }
  };

  const handleSelectedCampaigns = (campaigns) => {
    setSelectedCampaigns(campaigns);
    updateValues({ campaigns });
  };

  const handleSelectedExcludedRegions = (excludedRegions) => {
    setSelectedExcludedRegions(excludedRegions);
    updateValues({
      excludedRegions: excludedRegions.map((region) => ({
        regionCode: region,
      })),
    });
  };

  const campaignsOptions = useMemo(
    () =>
      campaigns.tags.map((campaign) => ({
        label: campaign.name,
        value: campaign.name,
      })),
    [campaigns.tags],
  );

  const excludedRegionOptions = useMemo(
    () =>
      getRegionNamesAndCode(tenant.brand).map((region) => ({
        label: region.name,
        value: region.code,
      })),
    [tenant.brand],
  );

  const curationStatus = [
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Not Curated', value: 'NOT_CURATED' },
  ];

  const status = getStatusByCurated(experience?.curationStatus);

  const handleSetImageHero = (imageId: number | string) => {
    setImageHero(imageId);
    updateValues({ heroImageId: imageId?.toString() });
  };

  const handleVendorBookingCutoffChange = (event) => {
    const value = parseInt(event.target.value || 0);
    updateValues({ vendorBookingCutoff: value });
  };

  const handleMarginChange = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null;
    updateValues({ margin: value });
  };

  const handleCommissionMarginChange = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null;
    updateValues({ commissionMargin: value });
  };

  const handleShowCommissionMarginField = (event) => {
    setShowCommissionMarginField(event.target.checked);
    if (!event.target.checked) {
      updateValues({ commissionMargin: null });
    }
  };

  const handleBookingCancellationCutoffChange = (event) => {
    const value = parseInt(event.target.value || 0);
    const cancellationInfo = payload?.cancellationInfo ?? experience?.cancellationInfo;
    cancellationInfo.daysCutoff = value;
    updateValues({ cancellationInfo });
  };

  const handleDeleteImage = (imageId: number | string) => {
    const updatedImages = images.filter((image) => String(image.id) !== String(imageId));
    const updatedImagesToRemove = [...imagesToRemove, String(imageId)];

    setImages(updatedImages);
    updateValues({ images: updatedImages });
    updateValues({ imagesToRemove: updatedImagesToRemove });
    setImagesToRemove(updatedImagesToRemove);

    if (String(imageHero) === String(imageId)) {
      setImageHero(null);
      updateValues({ heroImageId: undefined });
    }

    if (!areOriginalValues) {
      void persistImagesChange(updatedImages, 'delete');
    }
  };

  const persistImagesChange = useCallback(
    async (updatedImages: Array<Experiences.Image>, changeReason: 'upload' | 'delete') => {
      if (!experience?.id) {
        return;
      }

      const heroImageId = updatedImages.find((image) => image.isHero)?.id;

      try {
        await curationExperience(experience.id, tenant.brand, {
          curationStatus: values?.curationStatus ?? experience.curationStatus,
          heroImageId: heroImageId ? String(heroImageId) : undefined,
          images: updatedImages,
        });
      } catch (error) {
        console.error(`Failed to auto-save experience images after ${changeReason}:`, getErrorMessage(error));
      }
    },
    [experience?.curationStatus, experience?.id, tenant.brand, values?.curationStatus],
  );

  const handleImagesChange = (
    updatedImages: Array<Experiences.Image>,
    meta?: Parameters<NonNullable<FlatImageEditorProps['onItemsChange']>>[1],
  ) => {
    setImages(updatedImages);
    updateValues({ images: updatedImages });

    if (meta?.reason === 'upload' && !areOriginalValues) {
      void persistImagesChange(updatedImages, 'upload');
    }
  };

  const handleUploadImage = async ({ description, file, filename }: FlatImageUploadPayload) => {
    const uploadResponse = await uploadImage(file);
    const uploadedImageId = getUploadedImageId(uploadResponse);

    if (!uploadedImageId) {
      throw new Error('Image service did not return an image id');
    }

    const extension = file.type.split('/').pop() || 'jpg';

    return {
      id: uploadedImageId,
      description,
      descriptionEditable: true,
      displayId: uploadedImageId,
      filename,
      order: images.length + 1,
      previewUrl: `${window.configs.IMAGE_HOST}/${uploadedImageId}.${extension}`,
      publicImageId: uploadedImageId,
    };
  };

  const isRezdy = experience?.provider === EXP_PROVIDER_REZDY;
  const isLED = experience?.provider === EXP_PROVIDER_LED;
  const isDerbysoft = experience?.provider === EXP_PROVIDER_DERBYSOFT;
  const isKlook = experience?.provider === EXP_PROVIDER_KLOOK;
  const isAspire = experience?.provider === EXP_PROVIDER_ASPIRE;
  const isCollinson = experience?.provider === EXP_PROVIDER_COLLINSON;
  const isCustomLinc = experience?.provider === EXP_PROVIDER_CUSTOMLINC;
  const isHubby = experience?.provider === EXP_PROVIDER_HUBBY;

  const enableImagesUpload = isRezdy || isDerbysoft || isKlook || isAspire || isCollinson || isCustomLinc || isHubby;

  const transferEnabled = !!values?.features?.transfer;

  const hasPriceOverridden = values?.tickets?.some((ticket) => ticket.margin);

  const updateValuesFromCopyOffer = useCallback(
    (editableOfferDetails: EditableOfferDetails, selectedCopyOptions: Array<string>) => {
      if (selectedCopyOptions.includes(TITLE)) {
        updateValues({ title: editableOfferDetails.title });
      }

      if (selectedCopyOptions.includes(IMAGES)) {
        const heroImageId = editableOfferDetails.images.find((image) => image.isHero)?.id ?? null;

        updateValues({
          images: editableOfferDetails.images,
          imagesToRemove: undefined,
          heroImageId: heroImageId ? heroImageId.toString() : undefined,
        });
        setImages(editableOfferDetails.images);
        setImageHero(heroImageId);
        setImagesToRemove([]);
      }

      if (selectedCopyOptions.includes(DEAL_DESCRIPTION)) {
        updateValues({ dealDescription: editableOfferDetails.dealDescription });
      }

      if (selectedCopyOptions.includes(SHORT_DESCRIPTION)) {
        updateValues({ shortDescription: editableOfferDetails.shortDescription });
      }

      if (selectedCopyOptions.includes(INCLUDED)) {
        updateValues({ included: editableOfferDetails.included });
      }

      if (selectedCopyOptions.includes(THINGS_TO_KNOW)) {
        updateValues({ thingsToKnowNew: editableOfferDetails.thingsToKnowNew });
      }
    },
    [updateValues],
  );

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" fontWeight={600}>
          {experience?.title}
        </Typography>
        <Badge title={status?.title} background={status?.color} />
      </Stack>

      <Box display="flex" gap={3} alignItems="flex-start">
        <Stack spacing={2} sx={{ flex: 1, maxWidth: sidePanel ? undefined : 400 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Curation Status
            </Typography>
            <Select
              id="curation-status"
              size="small"
              fullWidth
              value={curationStatusValue.value}
              onChange={(e) => {
                const value = e.target.value;

                setCurationStatusValue({
                  label: curationStatus.find((status) => status.value === value).label,
                  value,
                });
                updateValues({ curationStatus: value });
              }}
            >
              {curationStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {!isLED && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Curation Comment
              </Typography>
              <Select
                id="curation-comment"
                size="small"
                fullWidth
                value={(values as ExperienceOfferWithCurationComment)?.curationComment || ''}
                onChange={(e) => {
                  updateValues({ curationComment: e.target.value });
                }}
                disabled={areOriginalValues}
                displayEmpty
              >
                <MenuItem value="">
                  <Typography color="text.secondary">None</Typography>
                </MenuItem>
                {curationCommentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Preview
            </Typography>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              href={getPreviewURL(tenant.value, experience?.id)}
              target="_blank"
            >
              {tenant.title}
            </Button>
          </Box>

          <Button
            variant="outlined"
            size="small"
            fullWidth
            onClick={() => setCopyModalOpen(true)}
            startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
          >
            Copy Content
          </Button>
        </Stack>

        {sidePanel}
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Images
        </Typography>

        <Box>
          <ExperienceCurationImages
            ref={imageEditorRef}
            handleImagesChange={handleImagesChange}
            items={images}
            imageHero={imageHero}
            isDescriptionEditable={!areOriginalValues}
            isUploadEnabled={enableImagesUpload && !areOriginalValues}
            onPendingUploadStateChange={onPendingUploadStateChange}
            handleSetImageHero={handleSetImageHero}
            handleDeleteImage={handleDeleteImage}
            handleUploadImage={handleUploadImage}
          />
        </Box>
      </Box>

      <Divider />

      {/* Tags & Collections */}
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
          Collections
        </Typography>
        {collectionsError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {collectionsError}
          </Alert>
        )}
        {selectedCollections.length > 0 ? (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedCollections.map((collection) => (
              <Chip
                key={collection.id}
                label={collection.theme}
                onDelete={!areOriginalValues ? () => handleDeleteCollection(collection.id) : undefined}
                deleteIcon={
                  !areOriginalValues ? (
                    <IconButton size="small" aria-label="delete collection">
                      <DeleteIcon />
                    </IconButton>
                  ) : undefined
                }
                disabled={areOriginalValues}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No collections assigned
          </Typography>
        )}
      </Box>

      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={editMultiSelect.campaigns}
                onChange={(e) =>
                  setEditMultiSelect({
                    ...editMultiSelect,
                    campaigns: e.target.checked,
                  })
                }
                disabled={areOriginalValues}
              />
            }
            label={<Typography variant="body2">Campaign tags</Typography>}
          />
          {campaigns.loading && <Spinner size={25} />}
          {campaigns.error && <Alert severity="error">{campaigns.message}</Alert>}
          {!campaigns.loading && !campaigns.error && (
            <Autocomplete
              size="small"
              multiple
              disableCloseOnSelect
              disabled={!editMultiSelect.campaigns}
              options={[SELECT_ALL_OPTION, ...campaignsOptions.map((option) => option.value)]}
              limitTags={3}
              onChange={(_, values) => {
                if (values.filter((item) => item === SELECT_ALL_OPTION).length === 1) {
                  handleSelectedCampaigns(campaignsOptions);
                } else {
                  handleSelectedCampaigns(values);
                }
              }}
              value={selectedCampaigns}
              renderInput={(params) => <TextField {...params} placeholder="Select tags" />}
            />
          )}
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={editMultiSelect.regions}
                onChange={(e) =>
                  setEditMultiSelect({
                    ...editMultiSelect,
                    regions: e.target.checked,
                  })
                }
                disabled={areOriginalValues}
              />
            }
            label={<Typography variant="body2">Excluded regions</Typography>}
          />
          <Autocomplete
            size="small"
            multiple
            disableCloseOnSelect
            disabled={!editMultiSelect.regions}
            options={[SELECT_ALL_OPTION, ...excludedRegionOptions.map((option) => option.value)]}
            limitTags={3}
            onChange={(_, values) => {
              if (values.filter((item) => item === SELECT_ALL_OPTION).length === 1) {
                handleSelectedExcludedRegions(excludedRegionOptions.map((option) => option.value));
              } else {
                handleSelectedExcludedRegions(values);
              }
            }}
            value={selectedExcludedRegions}
            renderInput={(params) => <TextField {...params} placeholder="Select regions" />}
            getOptionLabel={(option) =>
              excludedRegionOptions.find((region) => region.value === option)?.label || option
            }
          />
        </Box>
      </Box>

      <Divider />

      {/* Options */}
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          Options
        </Typography>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={values?.hideTimeSlots ?? false}
                onChange={(e) => updateValues({ hideTimeSlots: e.target.checked })}
                disabled={areOriginalValues}
              />
            }
            label={<Typography variant="body2">Hide time slots</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={values?.isNonRefundable ?? false}
                disabled={areOriginalValues}
                onChange={(e) => updateValues({ isNonRefundable: e.target.checked })}
              />
            }
            label={<Typography variant="body2">Non-refundable</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={values?.allowBuyNowBookLater ?? false}
                onChange={(e) => updateValues({ allowBuyNowBookLater: e.target.checked })}
                disabled={
                  areOriginalValues ||
                  transferEnabled ||
                  isRezdy ||
                  isDerbysoft ||
                  isKlook ||
                  isAspire ||
                  isCollinson ||
                  isCustomLinc
                }
              />
            }
            label={
              <Typography variant="body2">
                {transferEnabled ? 'Buy Now Book Later (disabled: Transfer enabled)' : 'Buy Now Book Later / Gifting'}
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={values?.allowGiftCard ?? true}
                onChange={(e) => updateValues({ allowGiftCard: e.target.checked })}
                disabled={areOriginalValues}
              />
            }
            label={<Typography variant="body2">Allow Gift Card</Typography>}
          />
          {transferEnabled && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!values?.features?.transfer?.preferredTransfer}
                  onChange={(e) =>
                    updateValues({
                      features: {
                        ...values.features,
                        transfer: { ...values.features.transfer, preferredTransfer: e.target.checked },
                      },
                    })
                  }
                />
              }
              label={<Typography variant="body2">Preferred transfer</Typography>}
            />
          )}
          {isLED && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={values?.ticketed ?? false}
                  disabled={areOriginalValues}
                  onChange={(e) => updateValues({ ticketed: e.target.checked })}
                />
              }
              label={<Typography variant="body2">Ticketed Experience</Typography>}
            />
          )}
          <FormControlLabel
            control={<Checkbox size="small" checked={hasPriceOverridden} disabled />}
            label={<Typography variant="body2">Price Overridden</Typography>}
          />
        </FormGroup>
      </Box>

      <Divider />

      {/* Booking & Pricing */}
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <TextField
          size="small"
          label="Cancellation Cutoff (days)"
          type="number"
          onChange={handleBookingCancellationCutoffChange}
          value={payload?.cancellationInfo?.daysCutoff || experience?.cancellationInfo?.daysCutoff || 0}
          required
        />
        <TextField
          size="small"
          label="Vendor Booking Cutoff (hours)"
          type="number"
          onChange={handleVendorBookingCutoffChange}
          value={payload?.vendorBookingCutoff || experience?.vendorBookingCutoff || 0}
          required
        />
        <TextField
          size="small"
          label="Profit Margin %"
          name="margin"
          onChange={handleMarginChange}
          value={values?.margin ?? 0}
          disabled={areOriginalValues}
          type="number"
          inputProps={{ max: 100 }}
          helperText="Affects the sales price of the deal"
        />
        <TextField
          size="small"
          label="Ticket Unit Label"
          name="ticketUnitLabel"
          placeholder='e.g. "Person", "Pass"'
          onChange={(e) => updateValues({ ticketUnitLabel: e.target.value })}
          value={values?.ticketUnitLabel ?? ''}
          disabled={areOriginalValues}
        />
      </Box>

      {!!isRezdy && (
        <>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCommissionMarginField}
                  disabled={areOriginalValues}
                  onChange={handleShowCommissionMarginField}
                />
              }
              label="This supplier does not provide cost price via Rezdy"
            />
          </FormGroup>

          {showCommissionMarginField && (
            <TextField
              name="commission-margin"
              onChange={handleCommissionMarginChange}
              label="Commission Margin %"
              value={values?.commissionMargin ?? 0}
              disabled={areOriginalValues}
              type="number"
              inputProps={{ max: 100 }}
              helperText="Equivalent to the percentage that will be deducted from the vendor's price as a commission for Luxury Escapes. This margin does not modify the deal's selling price."
              fullWidth
            />
          )}
        </>
      )}

      <ExperienceCurationOverview />

      <Box>
        <Typography fontWeight="bold" fontSize={16} mb={1}>
          Video
        </Typography>
        <Typography>Insert the Vimeo video ID</Typography>

        <Typography color="secondary">
          The ID is located at:{' '}
          <i>
            http://vimeo.com/
            <strong>1234567890</strong>
          </i>
        </Typography>

        <TextField
          onChange={(e) => updateValues({ video: e.target.value })}
          placeholder="Ex.: 1234567890"
          value={values?.video || ''}
          disabled={areOriginalValues}
        />
      </Box>
      <ExperienceOfferContentCopyModal
        isCopyModalVisible={copyModalOpen}
        hideCopyModal={() => setCopyModalOpen(false)}
        originalOfferDetails={experience}
        copyOfferData={updateValuesFromCopyOffer}
      />
      <ExperienceOfferVendorUpdatedWarningModal
        isModalVisible={vendorInfoChangedModalOpen}
        hideModal={() => setVendorInfoChangedModalOpen(false)}
        saveOperation={() => createExperience({ curationStatus: values?.curationStatus })}
      />
    </Stack>
  );
}
